import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import ImageCropModal from "./ImageCropModal";
import useTranslations from "../hooks/useTranslations";
import useUserLanguage from "../hooks/useUserLanguage";

export const SingleImageDropzone = ({
  fieldName,
  fieldValue,
  setFieldValues,
  onChange,
  onRemove,
  required,
  maxWidth,
  maxHeight,
}) => {
  const [showCropModal, setShowCropModal] = useState(false);
  const [fileToCrop, setFileToCrop] = useState(null);
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, def) => translations[key] || def;

  const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFileToCrop(acceptedFiles[0]);
      setShowCropModal(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
    noClick: true,
  });

  const toSrc = (val) => {
    if (!val) return null;

    // 1) File -> blob
    if (val instanceof File) return URL.createObjectURL(val);

    // 2) String -> farklı olasılıklar
    if (typeof val === "string") {
      // mutlak: blob:, data:, http(s):
      if (/^(blob:|data:|https?:\/\/)/i.test(val)) return val;

      // public asset (örn. /assets/..): direkt kullan
      if (val.startsWith("/assets/")) return val;

      // zaten /cdn ile başlıyorsa: API_BASE + val
      if (val.startsWith("/cdn/")) return `${API_BASE}${val}`;

      // Yükleme sonrası DB'ye /xxx-yyy.webp gibi GÖRELİ kaydedilenler:
      // Bunlar CDN’den dönmeli. Val başında / olsa da olmasa da /cdn prefixle.
      const path = val.startsWith("/") ? val : `/${val}`;
      return `${API_BASE}/cdn${path}`;
    }

    return null;
  };

  // Mevcut resmi yeniden kırpmak için
  const handleEdit = () => {
    if (!fieldValue) return; // kaydedilmiş resim yoksa
    setFileToCrop(fieldValue);
    setShowCropModal(true);
  };

  // Kırpma modalından dönünce
  const handleCropComplete = (croppedFile) => {
    // Nihai croppedFile’ı form state’e kaydediyoruz
    setFieldValues((prev) => ({ ...prev, [fieldName]: croppedFile }));
    onChange?.(croppedFile);
    setShowCropModal(false);

    setFileToCrop(null);
  };

  // Modal iptal
  const handleCancel = () => {
    setShowCropModal(false);
    setFileToCrop(null);
  };

  // Resmi kaldır
  const removeFile = (e) => {
    e.stopPropagation();
    setFieldValues((prev) => ({ ...prev, [fieldName]: null }));
    setShowCropModal(false);
    setFileToCrop(null);
    onRemove?.();
  };

  const renderPreview = () => {
    if (!fieldValue) return null;

    const imageSrc = toSrc(fieldValue);

    return (
      <div style={{ display: "inline-block" }}>
        <img
          src={imageSrc}
          alt="preview"
          style={{ maxWidth: "100%", maxHeight: "200px" }}
        />
        <div style={{ marginTop: "8px", textAlign: "center" }}>
          <button
            type="button"
            onClick={handleEdit}
            style={{
              background: "blue",
              color: "white",
              border: "none",
              borderRadius: "3px",
              cursor: "pointer",
              marginRight: "8px",
              padding: "4px 8px",
            }}
          >
            {getText("EDIT_BUTTON")}
          </button>
          <button
            type="button"
            onClick={removeFile}
            style={{
              background: "red",
              color: "white",
              border: "none",
              borderRadius: "3px",
              cursor: "pointer",
              padding: "4px 8px",
            }}
          >
            {getText("DELETE_BUTTON")}

          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        {...getRootProps()}
        className="dropzone border p-3 text-center"
        style={{ cursor: "pointer" }}
      >
        <input {...getInputProps()} required={required} />
        {isDragActive ? (
          <p>{getText("DROP_FILES_HERE")}</p>
        ) : fieldValue ? (
          renderPreview()
        ) : (
          <p onClick={open}>{getText("DRAG_OR_CLICK_UPLOAD")}</p>
        )}
      </div>

      {/* Kırpma modalı */}
      {showCropModal && fileToCrop && (
        <ImageCropModal
          key={
            fileToCrop instanceof File
              ? `${fileToCrop.name}-${fileToCrop.lastModified}`
              : String(fileToCrop) // URL/string ise
          }
          show={showCropModal}
          onHide={handleCancel}
          originalFile={fileToCrop}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          onComplete={handleCropComplete}
        />
      )}
    </>
  );
};
export const MultipleImageDropzone = ({
  fieldName,
  fieldValue,
  setFieldValues,
  required,
  maxWidth,
  maxHeight,
}) => {
  const [queue, setQueue] = useState([]);
  const [currentFile, setCurrentFile] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);

  // Düzenlenen mevcut fotoğrafın index'i
  const [editingIndex, setEditingIndex] = useState(null);

  // Sürüklenen fotoğrafın index'i
  const [draggedIndex, setDraggedIndex] = useState(null);

  const language = useUserLanguage();
  const { translations } = useTranslations(language);

  const getText = (key, defaultText = "") =>
    translations[key] || defaultText;

  const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

  const startCropping = useCallback((files) => {
    if (!files?.length) return;

    setEditingIndex(null);
    setQueue(files);
    setCurrentFile(files[0]);
    setShowCropModal(true);
  }, []);

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        startCropping(acceptedFiles);
      }
    },
    [startCropping]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
    },
    multiple: true,

    // Kartlara ve butonlara basınca dosya seçimi açılmasın.
    noClick: true,
  });

  const fileToPreviewSrc = (file) => {
    if (!file) return "";

    if (file instanceof File) {
      return URL.createObjectURL(file);
    }

    if (typeof file === "string") {
      if (/^(blob:|data:|https?:\/\/)/i.test(file)) {
        return file;
      }

      if (file.startsWith("/assets/")) {
        return file;
      }

      if (file.startsWith("/cdn/")) {
        return `${API_BASE}${file}`;
      }

      const path = file.startsWith("/") ? file : `/${file}`;
      return `${API_BASE}/cdn${path}`;
    }

    return "";
  };

  const closeCropModal = () => {
    setShowCropModal(false);
    setCurrentFile(null);
    setQueue([]);
    setEditingIndex(null);
  };

  const saveCropped = (croppedFile) => {
    /*
     * Mevcut fotoğraf düzenleniyorsa:
     * Yeni fotoğraf olarak sona eklemek yerine aynı index'i değiştir.
     */
    if (editingIndex !== null) {
      setFieldValues((prev) => {
        const currentImages = Array.isArray(prev[fieldName])
          ? [...prev[fieldName]]
          : [];

        currentImages[editingIndex] = croppedFile;

        return {
          ...prev,
          [fieldName]: currentImages,
        };
      });

      closeCropModal();
      return;
    }

    /*
     * Yeni yüklenen fotoğrafsa listeye ekle.
     */
    setFieldValues((prev) => {
      const currentImages = Array.isArray(prev[fieldName])
        ? prev[fieldName]
        : [];

      return {
        ...prev,
        [fieldName]: [...currentImages, croppedFile],
      };
    });

    /*
     * Çoklu yüklemede sıradaki fotoğrafa geç.
     */
    const newQueue = queue.slice(1);
    setQueue(newQueue);

    if (newQueue.length > 0) {
      setCurrentFile(newQueue[0]);
    } else {
      closeCropModal();
    }
  };

  const cancelCrop = () => {
    /*
     * Mevcut fotoğraf düzenleme işlemi iptal edildiyse
     * doğrudan modalı kapat.
     */
    if (editingIndex !== null) {
      closeCropModal();
      return;
    }

    /*
     * Yeni çoklu yüklemedeysek iptal edilen dosyayı geç
     * ve sıradaki dosyaya devam et.
     */
    const newQueue = queue.slice(1);
    setQueue(newQueue);

    if (newQueue.length > 0) {
      setCurrentFile(newQueue[0]);
    } else {
      closeCropModal();
    }
  };

  const editFile = (e, item, index) => {
    e.preventDefault();
    e.stopPropagation();

    setEditingIndex(index);
    setCurrentFile(item);
    setQueue([]);
    setShowCropModal(true);
  };

  const removeFile = (e, index) => {
    e.preventDefault();
    e.stopPropagation();

    setFieldValues((prev) => {
      const currentImages = Array.isArray(prev[fieldName])
        ? prev[fieldName]
        : [];

      return {
        ...prev,
        [fieldName]: currentImages.filter(
          (_, imageIndex) => imageIndex !== index
        ),
      };
    });
  };

  /*
   * Fotoğrafı bir sıradan başka sıraya taşı.
   */
  const moveImage = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    setFieldValues((prev) => {
      const currentImages = Array.isArray(prev[fieldName])
        ? [...prev[fieldName]]
        : [];

      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= currentImages.length ||
        toIndex >= currentImages.length
      ) {
        return prev;
      }

      const [movedImage] = currentImages.splice(fromIndex, 1);
      currentImages.splice(toIndex, 0, movedImage);

      return {
        ...prev,
        [fieldName]: currentImages,
      };
    });
  };

  const handleDragStart = (e, index) => {
    e.stopPropagation();

    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const sourceIndex =
      draggedIndex !== null
        ? draggedIndex
        : Number(e.dataTransfer.getData("text/plain"));

    if (
      Number.isInteger(sourceIndex) &&
      sourceIndex !== targetIndex
    ) {
      moveImage(sourceIndex, targetIndex);
    }

    setDraggedIndex(null);
  };

  const images = Array.isArray(fieldValue) ? fieldValue : [];

  return (
    <>
      <div
        {...getRootProps()}
        className="dropzone border p-3"
        style={{
          cursor: "default",
          minHeight: "130px",
        }}
      >
        <input {...getInputProps()} required={required} />

        {isDragActive ? (
          <div className="text-center py-4">
            <p className="mb-0">
              {getText(
                "DROP_FILES_HERE",
                "Fotoğrafları buraya bırakın"
              )}
            </p>
          </div>
        ) : (
          <>
            {images.length > 0 && (
              <div
                className="d-flex flex-wrap gap-3"
                style={{
                  alignItems: "flex-start",
                }}
              >
                {images.map((item, index) => (
                  <div
                    key={
                      item instanceof File
                        ? `${item.name}-${item.lastModified}-${index}`
                        : `${String(item)}-${index}`
                    }
                    draggable
                    onDragStart={(e) =>
                      handleDragStart(e, index)
                    }
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={() => setDraggedIndex(null)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "relative",
                      width: "150px",
                      padding: "8px",
                      border:
                        draggedIndex === index
                          ? "2px dashed #2563eb"
                          : "1px solid #dddddd",
                      borderRadius: "8px",
                      background: "#ffffff",
                      textAlign: "center",
                      cursor: "grab",
                      opacity:
                        draggedIndex === index ? 0.55 : 1,
                    }}
                  >
                    {/* Sıra numarası */}
                    <div
                      style={{
                        position: "absolute",
                        top: "4px",
                        left: "4px",
                        zIndex: 2,
                        minWidth: "24px",
                        height: "24px",
                        padding: "0 6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "12px",
                        background: "#212529",
                        color: "#ffffff",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {index + 1}
                    </div>

                    <img
                      src={fileToPreviewSrc(item)}
                      alt={`Galeri fotoğrafı ${index + 1}`}
                      draggable={false}
                      style={{
                        width: "100%",
                        height: "105px",
                        objectFit: "cover",
                        borderRadius: "5px",
                        display: "block",
                      }}
                    />

                    <div
                      style={{
                        marginTop: "8px",
                        display: "flex",
                        justifyContent: "center",
                        gap: "4px",
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Sola taşı */}
                      <button
                        type="button"
                        disabled={index === 0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          moveImage(index, index - 1);
                        }}
                        title="Sola taşı"
                        style={{
                          border: "none",
                          borderRadius: "4px",
                          padding: "4px 8px",
                          cursor:
                            index === 0
                              ? "not-allowed"
                              : "pointer",
                          opacity: index === 0 ? 0.4 : 1,
                        }}
                      >
                        ←
                      </button>

                      {/* Sağa taşı */}
                      <button
                        type="button"
                        disabled={index === images.length - 1}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          moveImage(index, index + 1);
                        }}
                        title="Sağa taşı"
                        style={{
                          border: "none",
                          borderRadius: "4px",
                          padding: "4px 8px",
                          cursor:
                            index === images.length - 1
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            index === images.length - 1
                              ? 0.4
                              : 1,
                        }}
                      >
                        →
                      </button>

                      <button
                        type="button"
                        onClick={(e) =>
                          editFile(e, item, index)
                        }
                        style={{
                          background: "#2563eb",
                          color: "#ffffff",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        {getText("EDIT_BUTTON", "Düzenle")}
                      </button>

                      <button
                        type="button"
                        onClick={(e) =>
                          removeFile(e, index)
                        }
                        style={{
                          background: "#dc2626",
                          color: "#ffffff",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        {getText("DELETE_BUTTON", "Sil")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div
              className="text-center"
              style={{
                marginTop: images.length > 0 ? "16px" : "20px",
              }}
            >
       

              {images.length > 1 && (
                <div
                  style={{
                    marginTop: "8px",
                    color: "#6c757d",
                    fontSize: "12px",
                  }}
                >
                  Fotoğrafları sürükleyerek veya okları
                  kullanarak sıralayabilirsiniz.
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showCropModal && currentFile && (
        <ImageCropModal
          key={
            currentFile instanceof File
              ? `${currentFile.name}-${currentFile.lastModified}`
              : `${String(currentFile)}-${editingIndex ?? "new"}`
          }
          show={showCropModal}
          onHide={cancelCrop}
          originalFile={currentFile}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          onComplete={saveCropped}
        />
      )}
    </>
  );
};
